<template>
  <div
    class="c-landing-page-card"
    :class="cardClass"
    :style="cardStyles"
    :role="url ? 'button' : undefined"
    @click="handleClick"
  >
    <div
      class="c-landing-page-card__content"
      :class="[
        contentClass,
        (title && title.Alignment === 'center')
          ? 'c-landing-page-card__content--flex-container' : ''
      ]"
    >
      <h2
        v-if="title"
        :class="titleClass"
      >
        {{ title.Text }}
      </h2>
      <p
        v-if="subtitle"
        :class="subtitleClass"
      >
        {{ subtitle.Text }}
      </p>
      <div
        v-if="showArrow"
        class="c-landing-page-card__icon"
      />
      <div
        v-else-if="price && productHandle && type"
        :class="priceClass"
      >
        ${{ price }}
      </div>
    </div>
  </div>
</template>

<script>
import isMobile from '@mixins/isMobile';
import get from 'lodash/get'
import { UrlFilters } from '@mixins/cdnMixins'
export default {
  mixins: [
    //this.isMobile is true screen width < 900px
    isMobile,
    UrlFilters
  ],
  props: {
    productHandle: {
      type: String,
      default: "etched-classic-silicone-ring-azul-sugar-skull",
    },
    title: {
      type: Object,
      default: () => {},
    },
    subtitle: {
      type: Object,
      default: () => {},
    },
    textColor: {
      type: String,
      default: "white",
    },
    url: {
      type: String,
      default: "",
    },
    type: {
      type: String,
      default: "product",
    },
    showArrow: {
      type: Boolean,
      default: false,
    },
    backgroundImage: {
      type: Object,
      default: () => ({
        src: "",
        alt: "background image",
      }),
    },
    getProductFunction: {
      type: Function,
      required: true,
    },
    gradient: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: "small",
    },
    backgroundSize: {
      type: String,
      default: 'contain'
    }
  },
  data() {
    return {
      product: {},
      price: null
    };
  },
  computed: {
    backgroundImageSrc() {
      const backgroundImage = this.backgroundImage
      const src = get(backgroundImage, 'src')
      return this.$getImgUrl(src, { width: 800, format: 'webp' })
    },
    cardStyles() {
      const backgroundImageSrc = this.backgroundImageSrc
      const backgroundSize = this.backgroundSize
      return {
        'background-image': `url('${backgroundImageSrc}')`,
        'background-size': `${backgroundSize}`
      }
    },
    cardClass() {
      let result = ["c-landing-page-card--small"];
      const size = this.size;
      const type = this.type
      if (size != "small") {
        result = [`c-landing-page-card--${size}`];
      }
      if (!type) {
        result.push("c-landing-page-card--no-link")
      }
      return result;
    },
    contentClass() {
      let result = [];
      const gradient = this.gradient;
      if (gradient) {
        result.push("c-landing-page-card__content--gradient");
      }
      return result;
    },
    titleClass() {
      let results = [];
      const title = this.title;
      const headingStyle = "t-heading-4";
      const textColor = this.textColor;
      if(!title.Alignment || title.Alignment !== 'center') {
        results.push("c-landing-page-card__title");
      }
      if (title.HeadingStyle) {
        results.push(title.HeadingStyle, `${title.HeadingStyle}--${textColor}`);
      } else {
        results.push(`${headingStyle}--` + textColor);
        results.push("t-heading-4");
      }

      if (title.MarginBottom) {
        if (this.isMobile) {
          results.push(`u-margin-bottom-x-small`);
        } else {
          results.push(`u-margin-bottom-${title.MarginBottom}`);
        }
      }

      return results;
    },
    subtitleClass() {
      let results = ["c-landing-page-card__subtitle"];
      const subtitle = this.subtitle;
      const textColor = this.textColor;
      if (subtitle.HeadingStyle) {
        results.push(subtitle.HeadingStyle);
      } else {
        results.push("t-heading-4");
      }

      if (subtitle.MarginBottom) {
        results.push(`u-margin-bottom-${subtitle.MarginBottom}`);
      }

      results.push("t-heading-4--" + textColor);
      return results;
    },
    priceClass() {
      let results = ["c-landing-page-card__price", "t-heading-7"];
      const textColor = this.textColor;
      results.push("t-heading-7--" + textColor);
      return results;
    },
  },
  watch: {
    productHandle() {
      if (this.productHandle) {
        this.getPrice();
      }
    },
    type() {
      if (this.type) {
        this.getPrice();
      }
    },
    showArrow() {
      if (!this.showArrow && this.productHandle && this.type) {
        this.getPrice();
      }
    },
  },
  mounted() {
    if (this.productHandle) {
      this.getPrice();
    }
  },
  methods: {
    handleClick() {
      if(this.url) {
        this.$router.push(this.url)
      } else {
        return
      }
    },
    async getPrice() {
      const productHandle = this.productHandle;
      const type = this.type;
      if (type == "collection" && productHandle) {
        try {
          let collectionProducts = await this.$api.getCollectionProducts({handle: productHandle})
          let prices = collectionProducts.map(e => e.minPrice)
          this.price = Math.min(...prices)
        } catch (err) {
          console.error("[content-product-card]", err);
        }
      } else if (type == "product" && productHandle) {
        try {
          let product = await this.$api.getProduct({ handle: productHandle });
          this.price = product.variants[0].price
        } catch (err) {
          console.error("[content-product-card]", err);
        }
      } else {
        this.price = null
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.c-landing-page-card {
  background-repeat: no-repeat;
  position: relative;
  width: 100%;
  cursor: pointer;
  background-size: cover;
  background-position: center;
  &__content {
    position: absolute;
    height: 100%;
    width: 100%;
    padding: rem(20px);
    @include respond(tab-land) {
      display: flex;
      justify-content: flex-end;
      flex-direction: column;
    }
    &--gradient {
      bottom: 0;
      height: 50%;
      background: rgb(2, 0, 36);
      background: linear-gradient(
        0deg,
        rgba(0, 0, 0, 0.667) 10%,
        rgba(0, 0, 0, 0) 90%
      );
    }
    &--flex-container {
      display: flex;
      justify-content: center;
      align-items: flex-end;
      @include respond(tab-land) {
        justify-content: flex-end;
        align-items: center;
      }
    }
  }
  &--large {
    min-height: rem(500px);
    max-height: rem(500px);
  }
  &--small {
    height: rem(300px);
    max-height: rem(300px);
  }
  &--tiny {
    height: rem(200px);
    max-height: rem(200px);
  }
  &__title {
    position: absolute;
    bottom: rem(30px);
    left: rem(20px);
    text-shadow: 6px 7px 21px #000000;
    padding: 0 35px 0 0;
    @include respond(tab-land) {
      position: relative;
      bottom: auto;
      left: auto;
    }
  }
  &__subtitle {
    position: absolute;
    bottom: rem(20px);
    left: rem(20px);
    padding-right: 20px;
    @include respond(tab-land) {
      position: relative;
      bottom: auto;
      left: auto;
      padding: 0 35px 20px 0;
    }
  }
  &__icon {
    &:before {
      content: url("~@/assets/icons/chevron-white.svg");
      width: rem(20px);
      position: absolute;
      right: rem(20px);
      bottom: 0;
      transform: translateY(-50%);
      fill: white;
    }
  }
  &__price {
    position: absolute;
    bottom: rem(20px);
    right: rem(20px);
  }
}
</style>
